import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MotifAccordion,
  MotifAccordionContent,
  MotifAccordionTrigger,
  MotifToast,
} from "@ey-xd/motif-react";
import { CurrencyConfigurationType } from "../CaseSetupType";
import Button from "@/components/Form/Button/Button";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import EditIcon from "./EditIcon";
import { useCreateCaseMutation } from "@/services/cases/casesReducer";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/app/store";
import { resetCaseSetup, updateStage } from "../caseSetupSlice";
import { postCaseSetupData } from "../CaseSetupHelper";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import {
  CANCEL_BUTTON_VARIANT,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";

export default function ReviewForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const CaseSetupData = useSelector((state: RootState) => state.caseSetup);
  const [createCase] = useCreateCaseMutation();
  const { caseSetupForm, debtorData, currencyConfiguration } = CaseSetupData;
  const {
    caseName,
    caseDescription,
    caseShortName,
    caseTimeZoneName,
    caseDescriptionInFrench,
    caseNameInFrench,
    isFrenchSelected,
    notificationRecipients,
  } = caseSetupForm;
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [accordinoStatus, setAccordionStatus] = useState<any>({
    identifiersStatus: true,
    debtorStatus: true,
    currencyStatus: true,
  });

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [showMessage, setShowMessage] = useState({
    show: false,
    type: "error",
  });
  const moveToSelectedTab = (selectedTab: number) => {
    dispatch(updateStage(selectedTab));
  };
  function handleAccordionStatus(status: string) {
    setAccordionStatus({
      ...accordinoStatus,
      [status]: !accordinoStatus[status],
    });
  }
  const cancelBtnApproveHandler = () => {
    setIsVisible(false);
    navigate("/");
    dispatch(resetCaseSetup());
  };
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: true }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch]);
  const saveBtnHandler = async () => {
    setIsLoading(true);

    if (
      !CaseSetupData.caseSetupForm.caseName ||
      CaseSetupData.caseSetupForm.caseName.trim() === ""
    ) {
      setShowMessage({
        ...showMessage,
        show: true,
        type: "error",
      });
      setNotification({
        ...notification,
        show: true,
        message: "pages.caseSetup.reviewTab.validationMessage",
        type: "error",
      });
      setIsLoading(false);
      return;
    }
    dispatch(setDiscardEditMode({ editMode: false }));

    try {
      const createCaseData = postCaseSetupData(CaseSetupData);
      const responseApi = await createCase(createCaseData).unwrap();

      if (responseApi.caseId) {
        dispatch(resetCaseSetup());
        setShowMessage({ ...showMessage, show: true, type: "success" });
        setNotification({
          ...notification,
          show: true,
          message: "global.globalNotifications.createdSuccessfully",
          type: "success",
        });

        navigate(`/case/${responseApi.caseId}`, {
          state: { showMessage: true },
        });
        setIsLoading(false);
      } else {
        const error =
          responseApi.data?.message ||
          "global.globalNotifications.unExpectedError";
        setShowMessage({
          ...showMessage,
          show: true,
          type: "error",
        });
        setNotification({
          ...notification,
          show: true,
          message: error,
          type: "error",
        });
        setIsLoading(false);
        dispatch(setDiscardEditMode({ editMode: true }));
      }
    } catch (error: any) {
      const errorMessage = error.data;
      setNotification({
        ...notification,
        show: true,
        message:
          t(errorMessage) || "global.globalNotifications.unExpectedError",
        type: "error",
      });
      setShowMessage({ ...showMessage, show: true, type: "error" });
      setIsLoading(false);
      dispatch(setDiscardEditMode({ editMode: true }));
    }
  };

  const closeMessageHandler = () => {
    setShowMessage({ ...showMessage, show: false, type: "error" });
  };

  const renderMessage = () => {
    if (showMessage.show) {
      window.scrollTo(0, 0);
      return (
        <MotifToast
          onClose={closeMessageHandler}
          //@ts-ignore
          variant={showMessage.type}
        >
          {" "}
          {showMessage.type === "error"
            ? t(notification.message)
            : t("global.globalNotifications.updatedSuccessfully")}
        </MotifToast>
      );
    }
  };

  const renderCurrencyConfiguration = (
    currencyConfiguration: CurrencyConfigurationType
  ) => {
    console.log(currencyConfiguration, "currencyConfiguration");
    if (currencyConfiguration) {
      return (
        <>
          <div className="motif-row pad-l-40 pad-r-20">
            <div className="motif-col-lg-3">
              <div>
                <div>
                  <div className="title">
                    {t(
                      "pages.caseSetup.currencyConfiguration.formLabels.defaultCurrencyName"
                    )}
                  </div>
                  <div className="motif-body2-default-bold overFlowWrapBreakWord">
                    {currencyConfiguration.defaultCurrency}
                  </div>
                </div>
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div>
                <div>
                  <div className="title">
                    {t(
                      "pages.caseSetup.currencyConfiguration.formLabels.exchangeRate"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {currencyConfiguration.defaultExchangeRate}
                  </div>
                </div>
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div>
                <div>
                  <div className="title">
                    {t(
                      "pages.caseSetup.currencyConfiguration.formLabels.decimalPoint"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {currencyConfiguration.defaultDecimalPoint}
                  </div>
                </div>
              </div>
            </div>
            <div className="motif-col-lg-3">
              <div>
                <div>
                  <div className="title">
                    {t(
                      "pages.caseSetup.currencyConfiguration.formLabels.description"
                    )}
                  </div>
                  <div className="motif-body2-default-bold overFlowWrapBreakWord">
                    {currencyConfiguration.defaultDescription}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {currencyConfiguration.fieldArray.map((currency: any) => (
            <div className="motif-row pad-l-40 pad-r-20 mar-t-20">
              <div className="motif-col-lg-3">
                <div>
                  <div>
                    <div className="title">
                      {t(
                        "pages.caseSetup.currencyConfiguration.formLabels.additionalCurrency"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {currency.originalCurrency}
                    </div>
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-3">
                <div>
                  <div>
                    <div className="title">
                      {t(
                        "pages.caseSetup.currencyConfiguration.formLabels.exchangeRate"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {currency.originalExchangeRate}
                    </div>
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-3">
                <div>
                  <div>
                    <div className="title">
                      {t(
                        "pages.caseSetup.currencyConfiguration.formLabels.decimalPoint"
                      )}
                    </div>
                    <div className="motif-body2-default-bold">
                      {currency.originalDecimalPoint}
                    </div>
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-3">
                <div>
                  <div>
                    <div className="title">
                      {t(
                        "pages.caseSetup.currencyConfiguration.formLabels.description"
                      )}
                    </div>
                    <div className="motif-body2-default-bold overFlowWrapBreakWord">
                      {currency.originalDescription}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      );
    }
  };
  return (
    <>
      <div className="motif-container pad-l-40 pad-r-20">
        {renderMessage()}
        <div className="motif-row">
          <div className="motif-col-lg-12  main-container">
            <div className="motif-row t-b-padding-21">
              <div className="motif-col-xs-4">
                <p className="form-title">
                  {t("pages.caseSetup.reviewTab.title")}
                </p>
                <p className="form-description">
                  {t("pages.caseSetup.reviewTab.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="motif-row t-b-padding-21">
          <div className="motif-col-xs-4">
            <div
              style={{
                alignItems: "flex-start",
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              <MotifAccordion
                onClose={() => {}}
                onOpen={() => {}}
                style={{
                  flex: "1",
                  width: "50%",
                }}
                open={accordinoStatus.identifiersStatus}
                useChevronIcon={true}
                alignIconRight={true}
                onClick={() => {
                  handleAccordionStatus("identifiersStatus");
                }}
              >
                <MotifAccordionTrigger>
                  <div
                    className="mar-t-10"
                    onClick={() => moveToSelectedTab(0)}
                  >
                    <EditIcon />
                  </div>
                  {t("pages.caseSetup.caseDetails.caseIdentifiersEnglish")}
                </MotifAccordionTrigger>
                <MotifAccordionContent>
                  <div className="motif-row">
                    <div className="motif-col-lg-4">
                      <div>
                        <div className="title">
                          {t("pages.caseSetup.caseDetails.formLabels.caseName")}
                        </div>
                        <div className="motif-body2-default-bold overFlowWrapBreakWord">
                          {caseName}
                        </div>
                      </div>
                    </div>
                    <div className="motif-col-lg-4">
                      <div>
                        <div className="title">
                          {t(
                            "pages.caseSetup.caseDetails.formLabels.shortName"
                          )}
                        </div>
                        <div className="motif-body2-default-bold overFlowWrapBreakWord">
                          {caseShortName}
                        </div>
                      </div>
                    </div>
                    <div className="motif-col-lg-4">
                      <div>
                        <div className="title">
                          {t(
                            "pages.caseSetup.caseDetails.formLabels.caseDescription"
                          )}
                        </div>
                        <div className="motif-body2-default-bold overFlowWrapBreakWord">
                          {caseDescription}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="motif-row pad-t-20">
                    <div className="motif-col-lg-4">
                      <div>
                        <div className="title">
                          {t("pages.caseSetup.caseDetails.formLabels.timeZone")}
                        </div>
                        <div className="motif-body2-default-bold">
                          {caseTimeZoneName}
                        </div>
                      </div>
                    </div>
                    <div className="motif-col-lg-4">
                      <div>
                        <div className="title">
                          {t(
                            "pages.caseSetup.caseDetails.formLabels.notificationRecipients"
                          )}
                        </div>
                        <div className="motif-body2-default-bold">
                          {notificationRecipients}
                        </div>
                      </div>
                    </div>
                  </div>
                  {isFrenchSelected && (
                    <div className="motif-row mar-t-20">
                      <div className="motif-col-lg-4">
                        <div>
                          <div className="title">
                            {t(
                              "pages.caseSetup.caseDetails.formLabels.caseNameFrench"
                            )}
                          </div>
                          <div className="motif-body2-default-bold overFlowWrapBreakWord">
                            {caseNameInFrench}
                          </div>
                        </div>
                      </div>
                      <div className="motif-col-lg-6">
                        <div>
                          <div className="title">
                            {t(
                              "pages.caseSetup.caseDetails.formLabels.caseDescriptionFrench"
                            )}
                          </div>
                          <div className="motif-body2-default-bold overFlowWrapBreakWord">
                            {caseDescriptionInFrench}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </MotifAccordionContent>
              </MotifAccordion>
            </div>
          </div>
        </div>
        <div className="motif-row t-b-padding-21 mar-t-30">
          <div className="motif-col-xs-4">
            <div
              style={{
                alignItems: "flex-start",
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              <MotifAccordion
                onClose={() => {}}
                onOpen={() => {}}
                style={{
                  flex: "1",
                  width: "50%",
                }}
                open={accordinoStatus.debtorStatus}
                useChevronIcon={true}
                alignIconRight={true}
                onClick={() => {
                  handleAccordionStatus("debtorStatus");
                }}
              >
                <MotifAccordionTrigger>
                  <div
                    className="mar-t-10"
                    onClick={() => moveToSelectedTab(1)}
                  >
                    <EditIcon />
                  </div>
                  {t("pages.caseSetup.debtorInformation.title")}
                </MotifAccordionTrigger>
                <MotifAccordionContent>
                  {debtorData.map((debtor, colIndex) => (
                    <div
                      className={`motif-row ${colIndex > 0 && "mar-t-20"}`}
                      key={colIndex}
                    >
                      <div className="motif-col-lg-4">
                        <div>
                          <div className="title">
                            {t(
                              "pages.caseSetup.debtorInformation.formLabels.debtorName"
                            )}
                          </div>
                          <div className="motif-body2-default-bold overFlowWrapBreakWord">
                            {debtor.legalDebtorName}
                          </div>
                        </div>
                      </div>
                      <div className="motif-col-lg-6">
                        <div>
                          <div className="title">
                            {t(
                              "pages.caseSetup.debtorInformation.formLabels.description"
                            )}
                          </div>
                          <div className="motif-body2-default-bold overFlowWrapBreakWord">
                            {debtor.debtorDescription}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </MotifAccordionContent>
              </MotifAccordion>
            </div>
          </div>
        </div>
        <div className="motif-row t-b-padding-21 mar-t-30">
          <div className="motif-col-xs-4">
            <div
              style={{
                alignItems: "flex-start",
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              <MotifAccordion
                onClose={() => {}}
                onOpen={() => {}}
                style={{
                  flex: "1",
                  width: "50%",
                }}
                open={accordinoStatus.currencyStatus}
                useChevronIcon={true}
                alignIconRight={true}
                onClick={() => {
                  handleAccordionStatus("currencyStatus");
                }}
              >
                <MotifAccordionTrigger>
                  <div
                    className="mar-t-10"
                    onClick={() => moveToSelectedTab(2)}
                  >
                    <EditIcon />
                  </div>{" "}
                  {t("pages.caseSetup.currencyConfiguration.title")}
                </MotifAccordionTrigger>
                <MotifAccordionContent>
                  {renderCurrencyConfiguration(currencyConfiguration)}
                </MotifAccordionContent>
              </MotifAccordion>
            </div>
          </div>
        </div>
        <div className="button-container sticky-footer">
          <Button
            label={t("global.globalActions.save")}
            onClickHandler={() => saveBtnHandler()}
            type={isLoading ? "loader" : "submit"}
            disabled={isLoading}
            variant={SAVE_BUTTON_VARIANT}
          />
          <Button
            type="button"
            label={t("global.globalActions.cancel")}
            variant={CANCEL_BUTTON_VARIANT}
            className="cancelBtn"
            onClickHandler={() => {
              dispatch(setDiscardEditMode({ editMode: false }));
              setIsVisible(true);
            }}
          />
        </div>
        <ConfirmationModal
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          cancelBtnApproveHandler={cancelBtnApproveHandler}
          cancelBtnRejectHandler={() => {
            dispatch(setDiscardEditMode({ editMode: true }));
            setIsVisible(false);
          }}
        />
      </div>
    </>
  );
}
